/**
 * Patient communications — the grounding rule.
 *
 * This is the most important constraint in the product. The demonstration's
 * product review put it plainly: the generator must never default to
 * "no fasting required", or any other service-specific instruction, when the
 * approved source did not say so. That specific failure is the statistically
 * likely model completion, which is exactly why it needs a deterministic
 * control and not just a well-worded prompt.
 *
 * Two layers implement it, both on the server:
 *   1. Prompt construction passes null fields as an explicit "not available,
 *      emit the placeholder" instruction. Omitting them invites invention.
 *   2. `validateGrounding()` re-checks the output afterwards and blocks it.
 *
 * Layer 2 is the one that goes in the DCB0129 hazard log as a control, because
 * it does not depend on the model behaving.
 */

export const MISSING =
  'Not provided in the approved source — add local information before generating this section.';

export const PROFILE_FIELDS = [
  ['purpose', 'Purpose'],
  ['happens', 'What will happen'],
  ['duration', 'Appointment duration'],
  ['preparation', 'Preparation'],
  ['bring', 'What to bring'],
  ['location', 'Location'],
  ['contact', 'Contact details'],
  ['cancellation', 'Cancellation process'],
  ['next', 'What happens next'],
] as const;

export type ProfileFieldKey = (typeof PROFILE_FIELDS)[number][0];

export const PROFILE_FIELD_KEYS = PROFILE_FIELDS.map(([k]) => k) as ProfileFieldKey[];

export const PROFILE_FIELD_LABELS = Object.fromEntries(PROFILE_FIELDS) as Record<
  ProfileFieldKey,
  string
>;

export const COMM_FORMATS = ['letter', 'sms', 'guide', 'what-to-expect'] as const;
export type CommFormat = (typeof COMM_FORMATS)[number];

export const COMM_FORMAT_LABELS: Record<CommFormat, string> = {
  letter: 'Invitation letter',
  sms: 'Text message',
  guide: 'Appointment guide',
  'what-to-expect': 'What to expect',
};

export const COMM_STATUS = ['draft', 'pending', 'approved', 'blocked'] as const;
export type CommStatus = (typeof COMM_STATUS)[number];

/**
 * Phrases that assert something specific about a service. If one of these
 * appears in generated output and the corresponding profile field was null,
 * the model has invented an instruction and the output must be blocked.
 *
 * Deliberately over-broad. A false positive costs a human ten seconds; a false
 * negative can reach a patient.
 */
export const SERVICE_ASSERTION_PATTERNS: { field: ProfileFieldKey; pattern: RegExp; why: string }[] = [
  // Preparation — the canonical failure. "No special preparation is needed" is
  // the single most likely fabrication and the most consequential.
  { field: 'preparation', pattern: /\bno (special |particular )?preparation\b/i, why: 'asserts no preparation is needed' },
  { field: 'preparation', pattern: /\bnothing to (do|prepare)\b/i, why: 'asserts nothing to prepare' },
  { field: 'preparation', pattern: /\b(you (do not|don't) need to|no need to) (fast|prepare)\b/i, why: 'asserts no fasting or preparation' },
  { field: 'preparation', pattern: /\bfast(ing)?\b/i, why: 'gives a fasting instruction' },
  { field: 'preparation', pattern: /\b(eat|drink|food|water)\b.{0,40}\b(before|prior to)\b/i, why: 'gives an eating or drinking instruction' },
  { field: 'preparation', pattern: /\b(take|stop|continue|withhold)\b.{0,30}\bmedication\b/i, why: 'gives a medication instruction' },

  { field: 'bring', pattern: /\b(bring|remember to bring)\b/i, why: 'tells the patient what to bring' },
  { field: 'bring', pattern: /\bnothing (to|you need to) bring\b/i, why: 'asserts nothing needs bringing' },

  { field: 'duration', pattern: /\b\d+\s*(minute|min|hour|hr)s?\b/i, why: 'states an appointment duration' },
  { field: 'duration', pattern: /\b(about|around|approximately)\s+(half|an?)\s+(an?\s+)?hour\b/i, why: 'states an appointment duration' },

  { field: 'location', pattern: /\b(room|floor|entrance|reception|building|wing|clinic room)\s+\w+/i, why: 'states a location' },
  { field: 'location', pattern: /\b(ground|first|second|third)\s+floor\b/i, why: 'states a location' },

  { field: 'contact', pattern: /\b0\d[\d\s-]{8,}\b/, why: 'gives a telephone number' },
  { field: 'contact', pattern: /\b[\w.+-]+@[\w-]+\.[\w.]+\b/, why: 'gives an email address' },

  { field: 'cancellation', pattern: /\b(cancel|reschedule|rearrange)\b/i, why: 'gives a cancellation instruction' },
  { field: 'cancellation', pattern: /\bgive us\b.{0,20}\bnotice\b/i, why: 'states a notice period' },

  { field: 'happens', pattern: /\b(blood test|blood sample|urine sample|foot check|eye (test|screening)|weight|examination)\b/i, why: 'states what will happen at the appointment' },

  { field: 'next', pattern: /\b(results|follow[- ]up|next appointment)\b.{0,40}\b(within|in)\s+\d+/i, why: 'states a timescale for what happens next' },
];

export interface GroundingViolation {
  field: ProfileFieldKey;
  fieldLabel: string;
  /** Every reason this field was flagged, deduplicated. */
  why: string;
  /** The sentence the offending text sits in. */
  excerpt: string;
  /** The exact words that matched, so the reader can see what to remove. */
  matched: string;
}

/**
 * The sentence containing an offset, rather than a fixed character window.
 *
 * A window cuts mid-word and buries the offending phrase in unrelated text,
 * which is the opposite of useful to the person deciding whether to approve.
 */
function sentenceAround(text: string, at: number, length: number): string {
  const before = text.slice(0, at);
  const start = Math.max(
    before.lastIndexOf('. '), before.lastIndexOf('\n'),
    before.lastIndexOf('? '), before.lastIndexOf('! '),
  );
  const from = start === -1 ? 0 : start + 1;

  const after = text.slice(at + length);
  const ends = ['. ', '\n', '? ', '! ']
    .map((s) => after.indexOf(s))
    .filter((i) => i !== -1);
  const to = ends.length ? at + length + Math.min(...ends) + 1 : text.length;

  const sentence = text.slice(from, to).trim();
  // A very long sentence still needs bounding, but on a word edge.
  return sentence.length > 220
    ? sentence.slice(0, 220).replace(/\s\S*$/, '') + '…'
    : sentence;
}

/**
 * Deterministic post-generation check. Runs on every generated communication
 * and every regeneration, before anything is shown as a draft.
 *
 * `filled` is the set of profile fields that actually have approved content.
 * Anything not in that set may not be asserted about.
 */
export function validateGrounding(
  text: string,
  filled: ReadonlySet<ProfileFieldKey>,
): GroundingViolation[] {
  const violations: GroundingViolation[] = [];

  // Two kinds of line assert nothing and must not be scanned.
  //
  // The placeholder itself contains words the patterns match. So do section
  // headings: a letter that says "What to bring" and then the placeholder
  // underneath is correctly grounded, but the heading alone would trip the
  // `bring` pattern. Flagging it would block well-formed letters, and a
  // control that fires on correct output is one people learn to switch off.
  const headings = new Set(
    Object.values(PROFILE_FIELD_LABELS).map((l) => l.toLowerCase()),
  );
  const isHeading = (line: string) => {
    const bare = line.trim().replace(/[:*#\-—–]+$/g, '').replace(/^[*#\-—–\s]+/, '').trim();
    if (!bare || bare.length > 40) return false;
    const lower = bare.toLowerCase();
    return headings.has(lower) || headings.has(lower.replace(/^what (to|will) /, 'what $1 '));
  };

  const body = text
    .split('\n')
    .filter((line) => !line.includes(MISSING) && !isHeading(line))
    .join('\n');

  // Several patterns can match the same field — "Room 2" and "first floor"
  // are both location assertions. Reporting them as separate violations makes
  // the same field appear twice with near-identical excerpts, which reads as
  // noise rather than as two findings. One entry per field, reasons combined.
  const byField = new Map<ProfileFieldKey, { reasons: string[]; at: number; matched: string }>();

  for (const { field, pattern, why } of SERVICE_ASSERTION_PATTERNS) {
    if (filled.has(field)) continue;
    const match = body.match(pattern);
    if (!match) continue;

    const existing = byField.get(field);
    if (existing) {
      if (!existing.reasons.includes(why)) existing.reasons.push(why);
      continue;
    }
    byField.set(field, { reasons: [why], at: match.index ?? 0, matched: match[0] });
  }

  for (const [field, found] of byField) {
    violations.push({
      field,
      fieldLabel: PROFILE_FIELD_LABELS[field],
      why: found.reasons.join('; '),
      excerpt: sentenceAround(body, found.at, found.matched.length),
      matched: found.matched.trim(),
    });
  }

  return violations;
}
